import React, { useCallback, useRef, useState } from "react";
import Model from "@tripian/model";
import { DirectionInfo, RouteResult } from "@tripian/react";
import DefaultStepCard from "../../../../components/DefaultStepCard/DefaultStepCard";
import EmptyStepSlot from "../../../../components/EmptyStepSlot/EmptyStepSlot";
import IStepAlternatives from "../../../../models/IStepAlternatives";

// Represents a deleted step slot that can be filled with alternatives
export interface DeletedStepSlot {
  id: string;
  order: number;
  timeFrom: string;
  timeTo: string;
  alternatives: Model.Poi[];
}

interface ITimelineSortableStepList {
  steps: Array<Model.Step>;
  focusStep: (step: Model.Step) => void;
  alternatives: IStepAlternatives[];
  alternativeDayNumbers: (alternativePois: Model.Poi[]) => number[][];
  alternativesStepId?: number;
  showAlternativesChange: (stepId: number, show: boolean) => void;
  hideScore?: boolean;
  hideStepsTime?: boolean;
  hideFeatures?: boolean;
  hideCuisine?: boolean;
  isWidget?: boolean;
  alternativePoiCardClicked: (
    stepId: number,
    alternativePoi: Model.Poi
  ) => void;
  alternativeReplace: (stepId: number, alternativePoi: Model.Poi) => void;
  memoizedThumbs: (step?: Model.Step) => number;
  thumbsClicked: (step: Model.Step, like: number) => void;
  timesClicked: (stepId: number, from: string, to: string) => void;
  loadingReactionStep: (stepId: number) => boolean;
  userReactionUndo: (step: Model.Step) => void;
  userReactionRemoveStep: (stepId: number, step: Model.Step) => Promise<any>;
  legs: RouteResult.ILeg[];
  legsOffset: number;
  bookaride?: (leg: RouteResult.ILeg) => void;
  gygTourIds: number[];
  bbTourIds: number[];
  viatorTourIds: string[];
  toristyTourIds: string[];
  toursLoading: boolean;
  onBookClick?: (step: Model.Step) => void;
  onAddStepFromSlot: (poiId: string, timeFrom: string, timeTo: string) => void;
  // Deleted slots management from parent
  deletedSlots: DeletedStepSlot[];
  onDeletedSlotsChange: (slots: DeletedStepSlot[]) => void;
  t: (value: Model.TranslationKey) => string;
}

const TimelineSortableStepList: React.FC<ITimelineSortableStepList> = ({
  steps,
  focusStep,
  alternatives,
  alternativeDayNumbers,
  alternativesStepId,
  showAlternativesChange,
  hideScore,
  hideStepsTime,
  hideFeatures,
  hideCuisine,
  isWidget,
  alternativePoiCardClicked,
  alternativeReplace,
  memoizedThumbs,
  thumbsClicked,
  timesClicked,
  loadingReactionStep,
  userReactionUndo,
  userReactionRemoveStep,
  legs,
  legsOffset,
  bookaride,
  gygTourIds,
  bbTourIds,
  viatorTourIds,
  toristyTourIds,
  toursLoading,
  onBookClick,
  onAddStepFromSlot,
  deletedSlots,
  onDeletedSlotsChange,
  t,
}) => {
  // Loading states for delete and add operations
  const [deletingStepId, setDeletingStepId] = useState<number | null>(null);
  const [addingSlotOrder, setAddingSlotOrder] = useState<number | null>(null);

  // Ref to always access latest deletedSlots in async callbacks (avoids stale closure)
  const deletedSlotsRef = useRef(deletedSlots);
  deletedSlotsRef.current = deletedSlots;

  // Handle step deletion - save the slot info AFTER delete completes
  const handleStepDelete = useCallback(
    (stepId: number, step: Model.Step, stepIndex: number) => {
      // Set loading state
      setDeletingStepId(stepId);

      // Get alternatives for this step BEFORE deleting
      const stepAlternatives = alternatives?.find(a => a.stepId === stepId);
      const alternativePois = stepAlternatives ? stepAlternatives.pois : [];

      // Add the deleted POI to the beginning of alternatives so it's always shown first
      const deletedPoi = step.poi;
      const alternativesWithDeletedPoi = deletedPoi
        ? [deletedPoi, ...alternativePois.filter(p => p.id !== deletedPoi.id)]
        : alternativePois;

      // Prepare the slot info - use stepIndex (array position) not step.order
      const newSlot: DeletedStepSlot = {
        id: `${Date.now()}_${stepId}`,
        order: stepIndex,
        timeFrom: step.times.from || "09:00",
        timeTo: step.times.to || "10:00",
        alternatives: alternativesWithDeletedPoi,
      };

      // Call the actual delete and add slot AFTER it completes
      userReactionRemoveStep(stepId, step)
        .then(() => {
          onDeletedSlotsChange([...deletedSlotsRef.current, newSlot]);
        })
        .catch(() => {
          // ReadOnly trip or delete failed - don't add empty slot
        })
        .finally(() => {
          setDeletingStepId(null);
        });
    },
    [alternatives, userReactionRemoveStep, onDeletedSlotsChange]
  );

  // Handle selecting an alternative from empty slot
  const handleSelectAlternative = useCallback(
    (slot: DeletedStepSlot, poi: Model.Poi): Promise<void> => {
      // Set loading state
      setAddingSlotOrder(slot.order);

      // Add the step - return a promise
      return new Promise<void>((resolve) => {
        onAddStepFromSlot(poi.id, slot.timeFrom, slot.timeTo);
        // Remove the slot from deleted slots after a short delay to allow API to complete
        // The actual removal happens when the step is added and list re-renders
        setTimeout(() => {
          onDeletedSlotsChange(deletedSlotsRef.current.filter(s => s.id !== slot.id));
          setAddingSlotOrder(null);
          resolve();
        }, 1500);
      });
    },
    [onAddStepFromSlot, onDeletedSlotsChange]
  );

  // Combine steps and slots into a unified list, sorted by their position
  // Steps use their array index, slots use their stored order
  type RenderItem =
    | { type: "step"; step: Model.Step; index: number }
    | { type: "slot"; slot: DeletedStepSlot };

  const renderItems: RenderItem[] = [];

  // Add steps with their current index as position
  steps.forEach((step, index) => {
    renderItems.push({ type: "step", step, index });
  });

  // Add deleted slots - their order represents where they should appear
  deletedSlots.forEach(slot => {
    renderItems.push({ type: "slot", slot });
  });

  // Sort by position: steps by index, slots by order
  renderItems.sort((a, b) => {
    const posA = a.type === "step" ? a.index : a.slot.order;
    const posB = b.type === "step" ? b.index : b.slot.order;
    return posA - posB;
  });

  return (
    <ul className="p-0 m-0 flex flex-col">
      {renderItems.map((item, renderIndex) => {
        if (item.type === "slot") {
          // Render empty slot
          return (
            <li key={`slot-${item.slot.id}`} className="list-none my-2 mb-4">
              <EmptyStepSlot
                timeFrom={item.slot.timeFrom}
                timeTo={item.slot.timeTo}
                alternatives={item.slot.alternatives}
                onSelectAlternative={poi =>
                  handleSelectAlternative(item.slot, poi)
                }
                onDeleteSlot={() => {
                  onDeletedSlotsChange(deletedSlots.filter(s => s.id !== item.slot.id));
                }}
                isLoading={addingSlotOrder === item.slot.order}
              />
            </li>
          );
        }

        // Render step
        const { step, index: stepIndex } = item;
        const stepAltertives = alternatives?.find(
          a => a.stepId === step.id
        );
        const alternativePois = stepAltertives ? stepAltertives.pois : [];
        const alternativePoisDays: number[][] =
          alternativeDayNumbers(alternativePois);

        return (
          <li key={step.id} className="list-none">
            <div>
              <div className="relative w-full flex items-center">
                <div className="relative w-full">
                  {/* Loading overlay when deleting */}
                  {deletingStepId === step.id && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 animate-spin text-primary-color" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm font-medium text-gray-600">Removing...</span>
                      </div>
                    </div>
                  )}
                  <DefaultStepCard
                    step={step}
                    clicked={focusStep}
                    alternativePois={alternativePois}
                    alternativePoisDays={alternativePoisDays}
                    showAlternatives={alternativesStepId === step.id}
                    showAlternativesChange={showAlternativesChange}
                    hideScore={hideScore}
                    hideStepsTime={hideStepsTime}
                    hideFeatures={hideFeatures}
                    hideCuisine={hideCuisine}
                    isWidget={isWidget}
                    alternativePoiCardClicked={(
                      alternativePoi: Model.Poi
                    ) =>
                      alternativePoiCardClicked(step.id, alternativePoi)
                    }
                    alternativeReplace={(alternativePoi: Model.Poi) =>
                      alternativeReplace(step.id, alternativePoi)
                    }
                    thumbs={memoizedThumbs(step)}
                    thumbsClicked={(like: number) =>
                      thumbsClicked(step, like)
                    }
                    timesClicked={(from, to) =>
                      timesClicked(step.id, from, to)
                    }
                    thumbsLoading={loadingReactionStep(step.id)}
                    userReactionUndo={() => userReactionUndo(step)}
                    userReactionRemoveStep={() =>
                      handleStepDelete(step.id, step, stepIndex)
                    }
                    onBookClick={onBookClick}
                    gygTourIds={gygTourIds}
                    bbTourIds={bbTourIds}
                    viatorTourIds={viatorTourIds}
                    toristyTourIds={toristyTourIds}
                    toursLoading={toursLoading}
                    t={t}
                  />
                </div>
              </div>
              {legs[stepIndex + legsOffset] ? (
                <DirectionInfo
                  distance={
                    legs[stepIndex + legsOffset].distance.text
                  }
                  direction={
                    legs[stepIndex + legsOffset].duration.text
                  }
                  travel={legs[stepIndex + legsOffset].travel}
                  bookaride={bookaride ? () => bookaride(legs[stepIndex + legsOffset]) : undefined}
                  hideBookaRide={!window.tconfig.SHOW_BOOK_A_RIDE}
                  t={t}
                />
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default TimelineSortableStepList;
